import React, { useEffect } from 'react'
import BuildScenarioAccordion from './build_scenario_list/BuildScenarioAccordion'
import BuildScenarioTuners from './build_scenario_list/BuildScenarioTuners'
import {
  StandardButtonSecondary,
  RunButtonWithIcon,
} from '../../../ui_reusable/styles/Button.styles'
import {
  ScenarioListContainer,
  ButtonsContainer,
  ScenarioContent,
} from './styles/BuildScenarioList.styles'
import { useAccordionContext } from '../../../ui_reusable/BaseAccordion'
import useBuildScenarioStore from '../stores/buildScenarioStore'
import { useScenarioChangedExceptDriversAndStartValue } from '../stores/buildScenarioStore'

/**
 * ScenarioExpansionHandler - Component that handles automatic expansion of new scenarios
 * This component accesses the BaseAccordion context to programmatically expand new scenarios
 */
const ScenarioExpansionHandler = ({ scenarios }) => {
  const { activateAccordion } = useAccordionContext()
  const makeScenarioNotFresh = useBuildScenarioStore(
    (state) => state.makeScenarioNotFresh,
  )

  useEffect(() => {
    // Find newly created scenarios
    const newScenarios = scenarios.filter((scenario) => scenario.isNew)

    if (newScenarios.length > 0) {
      // Expand each new scenario after a short delay
      setTimeout(() => {
        newScenarios.forEach((scenario) => {
          const eventKey = `scenario-${scenario.id}`
          activateAccordion(eventKey)
          // Mark as no longer new after auto-expanding
          makeScenarioNotFresh(scenario.id)
        })
      }, 100)
    }
  }, [scenarios, activateAccordion, makeScenarioNotFresh])

  return null
}

const BuildScenarioList = ({ onSubmit, onReset, canCreateScenarios = true }) => {
  //const { scrollToPanel } = useScroll();

  // Subscribe to store data - only re-renders when scenario structure changes (IDs, names, isNew)
  const selectedModel = useBuildScenarioStore((state) => state.selectedModel)
  const scenarios = useScenarioChangedExceptDriversAndStartValue()

  // Subscribe to submit readiness flag
  const isReadyForSubmitFlag = useBuildScenarioStore(
    (state) => state.isReadyForSubmitFlag,
  )

  // Store actions
  const createScenario = useBuildScenarioStore((state) => state.createScenario)
  const makeScenarioNotFresh = useBuildScenarioStore(
    (state) => state.makeScenarioNotFresh,
  )
  const setScenarioName = useBuildScenarioStore(
    (state) => state.setScenarioName,
  )
  const deleteScenario = useBuildScenarioStore((state) => state.deleteScenario)
  const setReadyForSubmitFlagInstantly = useBuildScenarioStore(
    (state) => state.setReadyForSubmitFlagInstantly,
  )

  const handleAddScenario = () => {
    if (!canCreateScenarios) {
      return
    }
    if (scenarios.length < 8) {
      createScenario()
    }
  }

  const handleHeaderChange = (scenarioId, newName) => {
    setScenarioName(scenarioId, newName)
  }

  const handleScenarioToggle = (scenarioId, isActive) => {
    if (isActive) {
      // When opening, mark as no longer new
      makeScenarioNotFresh(scenarioId)
    }
  }

  const handleReset = () => {
    setReadyForSubmitFlagInstantly(false)
    if (onReset) {
      onReset()
    }
  }

  const handleRun = () => {
    if (!canCreateScenarios) {
      return
    }
    setReadyForSubmitFlagInstantly(false)
    if (onSubmit) {
      onSubmit()
    }
  }

  const handleDeleteScenario = (scenarioId) => {
    deleteScenario(scenarioId)
  }
  const getDisabledText = () => {
    if (!canCreateScenarios) {
      return 'Reactivate the project before creating a scenario.'
    }
    if (!selectedModel) {
      return 'Select a model to enable scenarios!'
    } else if (scenarios.length >= 8) {
      return 'Maximum of 8 scenarios reached!'
    }
    return ''
  }

  return (
    <div>
      <ScenarioListContainer>
        <BuildScenarioAccordion id="scenarios-accordion" defaultActiveKey={[]}>
          <ScenarioExpansionHandler scenarios={scenarios} />
          {scenarios.map((scenario) => {
            return (
              <BuildScenarioAccordion.Panel
                key={scenario.id}
                header={scenario.name}
                eventKey={`scenario-${scenario.id}`}
                isButton={false}
                onHeaderChange={(newName) =>
                  handleHeaderChange(scenario.id, newName)
                }
                onToggle={(isActive) =>
                  handleScenarioToggle(scenario.id, isActive)
                }
                onDeleteClick={() => handleDeleteScenario(scenario.id)}
              >
                <ScenarioContent>
                  <BuildScenarioTuners scenarioId={scenario.id} />
                </ScenarioContent>
              </BuildScenarioAccordion.Panel>
            )
          })}

          <BuildScenarioAccordion.Panel
            header="Add scenario"
            eventKey="add-new"
            scenarioStarted={false}
            onHeaderClick={handleAddScenario}
            isButton={true}
            disabled={
              !canCreateScenarios || !selectedModel || scenarios.length >= 8
            }
            disabledText={getDisabledText()}
          >
            <div style={{ padding: '1rem' }}>
              <p>Click the header to add a new scenario</p>
            </div>
          </BuildScenarioAccordion.Panel>
        </BuildScenarioAccordion>
      </ScenarioListContainer>

      <ButtonsContainer>
        <StandardButtonSecondary
          onClick={handleReset}
          disabled={!isReadyForSubmitFlag}
        >
          Reset All
        </StandardButtonSecondary>
        <RunButtonWithIcon
          onClick={handleRun}
          disabled={!canCreateScenarios || !isReadyForSubmitFlag}
          title={
            canCreateScenarios
              ? undefined
              : 'Reactivate the project before creating a scenario.'
          }
        >
          OK
        </RunButtonWithIcon>
      </ButtonsContainer>
    </div>
  )
}

export default BuildScenarioList
